// Geotagged field-photo pins, sourced from Ente ("Field Photos" album) via
// pipeline/field-photos/publish_field_photos.py — see that script and
// docs/deployment.md's v0.5.0 section for the full pipeline. Rendered as an
// ordinary OpenLayers vector layer, same as any other feature layer; the
// only thing distinguishing it from a WMS layer is that it's loaded once as
// GeoJSON (WFS in production, a local test fixture in dev — see
// src/config/env.ts's FIELD_PHOTOS_URL) rather than tiled.
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style';
import { FIELD_PHOTOS_URL } from '../config/env';

export interface PhotoProperties {
  filename: string;
  capture_date: string;
  uploader?: string;
  file_type?: string;
  file_size_bytes?: number;
  description?: string;
  /** platform_db.content_items id (v0.6.6) — the real path: PhotoDetailPanel fetches a
   * short-lived signed URL via GET /api/v1/content/{id}/download on demand, never a stored URL
   * here. Set on every real (non-fixture) pin published by publish_field_photos.py. */
  content_item_id?: string;
  /** Dev-only fixture path (public/fixtures/field-photos.test.geojson): direct data: URIs, no
   * content_item_id, no signing needed — see test_fixture below. Never set by the real pipeline. */
  thumbnail_url?: string;
  image_url?: string;
  test_fixture?: boolean;
}

const PIN_STYLE = new Style({
  image: new CircleStyle({
    radius: 8,
    fill: new Fill({ color: '#d94f63' }),
    stroke: new Stroke({ color: '#fff', width: 2 }),
  }),
});

export function makePhotoLayer() {
  const source = new VectorSource({
    url: FIELD_PHOTOS_URL,
    // The GeoJSON fixture/WFS response is plain lon/lat (EPSG:4326,
    // GeoJSON's default); reproject into the map's own EPSG:3857 view on
    // read, same as fromLonLat() does for the farm center in MapContext.
    format: new GeoJSON({ featureProjection: 'EPSG:3857' }),
  });
  const layer = new VectorLayer({
    source,
    style: PIN_STYLE,
    visible: true,
    properties: { id: 'photo:field-photos', title: 'Field Photos', kind: 'photo' },
  });
  // A missing/unreachable field-photos endpoint (no live GeoServer, or the
  // publishing pipeline hasn't run yet) must not crash the app — same
  // resilience posture as the WMS tile layers (MapView.tsx's
  // tileloaderror handling).
  source.on('featuresloaderror', () => {
    // eslint-disable-next-line no-console
    console.warn('Field Photos layer: could not load', FIELD_PHOTOS_URL);
  });
  return layer;
}
